import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Upload, X, Loader2, ImageIcon, RotateCw } from "lucide-react";
import { loadSettings, updateSettings } from "../lib/settings";
import { toast } from "sonner";
import { ActivityTypesEditor } from "./ActivityTypesEditor";

const resizeAndEncode = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const maxW = 500;
                const scale = Math.min(1, maxW / img.width);
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/png"));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

export const SettingsDialog = ({ open, onOpenChange }) => {
    const [firmaDatore, setFirmaDatore] = useState(null);
    const [rotateBack, setRotateBack] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            const s = await loadSettings();
            setFirmaDatore(s.firma_datore || null);
            setRotateBack(!!s.rotate_back);
            setLoading(false);
        })();
    }, [open]);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica un file immagine");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 5 MB)");
            return;
        }
        const dataUrl = await resizeAndEncode(file);
        setFirmaDatore(dataUrl);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateSettings({ firma_datore: firmaDatore, rotate_back: rotateBack });
            toast.success("Impostazioni salvate");
            onOpenChange(false);
        } catch (e) {
            toast.error("Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="settings-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Impostazioni Azienda</DialogTitle>
                    <DialogDescription>
                        Carica la firma del datore di lavoro. Verrà apposta automaticamente sul retro di ogni tesserino.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Firma del Datore di Lavoro
                            </div>
                            <div className="mt-2 flex h-32 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50">
                                {firmaDatore ? (
                                    <img
                                        src={firmaDatore}
                                        alt="Firma datore"
                                        className="max-h-28 max-w-full object-contain"
                                        data-testid="settings-firma-preview"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-1 text-slate-400">
                                        <ImageIcon className="h-6 w-6" />
                                        <span className="text-xs">Nessuna firma caricata</span>
                                    </div>
                                )}
                            </div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFile}
                                data-testid="settings-firma-input"
                            />
                            <div className="mt-3 flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileRef.current?.click()}
                                    className="gap-2"
                                    data-testid="settings-firma-upload"
                                >
                                    <Upload className="h-4 w-4" />
                                    {firmaDatore ? "Sostituisci" : "Carica firma"}
                                </Button>
                                {firmaDatore && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={() => setFirmaDatore(null)}
                                        className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                        data-testid="settings-firma-remove"
                                    >
                                        <X className="h-4 w-4" />
                                        Rimuovi
                                    </Button>
                                )}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                                PNG o JPG con sfondo trasparente. Verrà ridimensionata automaticamente. Se vuota viene usata la firma di default già caricata.
                            </p>
                        </div>

                        <div className="border-t border-slate-200 pt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Stampa
                            </div>
                            <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-[#376DB3]/50 hover:bg-[#5BAEDB]/10">
                                <Checkbox
                                    checked={rotateBack}
                                    onCheckedChange={(v) => setRotateBack(!!v)}
                                    className="mt-0.5"
                                    data-testid="settings-rotate-back"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-[#376DB3]">
                                        <RotateCw className="h-4 w-4" />
                                        Ruota retro 180°
                                    </div>
                                    <p className="mt-0.5 text-xs text-slate-600">
                                        Attiva se la tua stampante badge stampa fronte e retro in orientamenti speculari (lato lungo). Il retro verrà ruotato automaticamente nel PDF.
                                    </p>
                                </div>
                            </label>
                        </div>

                        <div className="border-t border-slate-200 pt-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Tipologie attività (Presenze)
                            </div>
                            <p className="mb-2 mt-0.5 text-xs text-slate-500">Elenco proposto quando assegni un cliente/cantiere a un dipendente. Le modifiche si salvano subito.</p>
                            <ActivityTypesEditor />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settings-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="settings-save"
                    >
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
